import React, { useRef, useImperativeHandle, useMemo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Grid } from "@mui/material";
import { TextField, Box, CheckboxField, Stack, Typography } from "@curves/ui-base";
import { SignBox } from "@icoach/components";
import DownloadCardAuthButton from "@icoach/components/creditCard/DownloadCardAuthButton";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import CreditCardFields from "@icoach/components/creditCard/CreditCardFields";
import useOwnerRelationState from "@icoach/components/creditCard/useOwnerRelationState";
import { CREDIT_CARD_TYPE } from "@icoach/constants/";

/**
 * 判斷是否顯示簽名欄位
 * @param {Object} params
 * @param {boolean} params.showSignature - 是否顯示簽名區（全域控制）
 * @param {boolean} params.isOwner - 是否為本人
 * @param {boolean} params.requireOwnerSignature - 本人是否需要簽名
 * @returns {boolean}
 */
const shouldShowSign = ({ showSignature, isOwner, requireOwnerSignature }) => {
	if (!showSignature) return false;
	return isOwner ? requireOwnerSignature : true;
};

const CREDIT_CARD_TYPE_WHITELIST = [0, null, ...Object.values(CREDIT_CARD_TYPE)];

const CreditCard = React.forwardRef(
	(
		{
			className,
			defaultValue = {
				creditCardNo: null,
				cardExpiryDate: null,
				cardName: null,
				creditCardBankName: null,
				creditCardType: null,
				cardRelation: null,
				signImg: null,
			},
			errorModule = {},
			required = false,
			readOnly = false,
			requireOwnerSignature = false,
			showSignature = false,
			showCardRelation = false,
			showBankName = false,
			showAuthorizationDownload = false,
		},
		ref
	) => {
		const normalizedDefaultValue = useMemo(
			() => ({
				...defaultValue,
				creditCardType: CREDIT_CARD_TYPE_WHITELIST.includes(defaultValue?.creditCardType) ? defaultValue?.creditCardType : null,
			}),
			[defaultValue],
		);
		const { isOwner, cardRelation, setIsOwner, updateCardRelation } = useOwnerRelationState(defaultValue?.cardRelation);
		const cardFieldsRef = useRef(null);
		const signImgRef = useRef(null);
		const cardRelationRef = useRef(null);

		const showSign = shouldShowSign({ showSignature, isOwner, requireOwnerSignature });

		const showFields = useMemo(
			() => {
				return !showBankName ? ["creditCardNo", "cardExpiryDate", "cardName"] : undefined;
			},
			// eslint-disable-next-line
			[showBankName]
		);

		useImperativeHandle(
			ref,
			() => ({
				getResult: () => {
					const cardResult = cardFieldsRef.current?.getResult?.() || {};

					cardResult.signImg = signImgRef.current?.getResult?.() || "";

					if (showCardRelation) {
						cardResult.cardRelation = cardRelationRef.current?.getResult?.() || cardRelation;
					}

					return cardResult;
				},
				isError: () => refIsRequiredError(cardFieldsRef, signImgRef, cardRelationRef),
			}),
			// eslint-disable-next-line
			[cardRelation]
		);

		return (
			<Box className={clsx("credit-card", className)}>
				<Grid container spacing={2}>
					<Grid xs={12} item>
						<CreditCardFields
							ref={cardFieldsRef}
							defaultValue={normalizedDefaultValue}
							errorModule={errorModule}
							showFields={showFields}
							readOnly={readOnly}
							required={required}
						/>
					</Grid>
					{showCardRelation && (
						<React.Fragment>
							<Grid xs={12} item>
								<Stack justifyContent={"start"} alignItems={"start"}>
									<CheckboxField key={isOwner.toString()} defaultValue={isOwner ? [isOwner] : []} onChange={(e, value) => setIsOwner(value)} required row>
										<CheckboxField.Checkbox value={true} label={"信用卡為本人持有"} readOnly={readOnly} />
									</CheckboxField>
									{!isOwner && (
										<TextField
											label={"卡片關係為"}
											ref={cardRelationRef}
											value={cardRelation || ""}
											{...getErrorModuleStatus(errorModule, "cardRelation")}
											onChange={e => updateCardRelation(e.target.value)}
											readOnly={readOnly}
											direction={"row"}
										/>
									)}
								</Stack>
							</Grid>
						</React.Fragment>
					)}
					{showSign && (
						<Grid xs={6} item>
							<SignBox title={"持卡人簽名"} ref={signImgRef} defaultValue={normalizedDefaultValue?.signImg} {...getErrorModuleStatus(errorModule, "signImg")} readOnly={readOnly} required />
							<Typography variant={"body2"} className={"font-color-3 mb-2"}>
								(須與信用卡背面簽名一致)
							</Typography>
							{showAuthorizationDownload && <DownloadCardAuthButton />}
						</Grid>
					)}
				</Grid>
			</Box>
		);
	}
);

CreditCard.propTypes = {
	className: PropTypes.string,
	defaultValue: PropTypes.shape({
		creditCardNo: PropTypes.string,
		cardExpiryDate: PropTypes.string,
		cardName: PropTypes.string,
		creditCardBankName: PropTypes.string,
		creditCardType: PropTypes.oneOf(CREDIT_CARD_TYPE_WHITELIST), // VISA, MasterCard, JCB, OTHER
		cardRelation: PropTypes.string,
		signImg: PropTypes.string,
	}),
	errorModule: PropTypes.object,
	requireOwnerSignature: PropTypes.bool,
	showSignature: PropTypes.bool,
	showCardRelation: PropTypes.bool,
	showBankName: PropTypes.bool,
	required: PropTypes.bool,
	readOnly: PropTypes.bool,
	showAuthorizationDownload: PropTypes.bool,
};

export default CreditCard;
